/**
 * Extract a clear, user-facing message from any error thrown by the generated API
 * client, an axios error, or a plain Error.
 *
 * Why this exists: the generated client (`api/core/request.ts`) throws an `ApiError`
 * whose `.message` is a generic status label ("Bad Request", "Forbidden", …) or a
 * "Generic Error: …" dump. The real, backend-supplied reason lives in the ABP payload
 * at `error.body.error.message` (with field errors under `validationErrors` and coded
 * business errors already localized server-side). Handlers that surface `err.message`
 * therefore show "there was an exception" instead of the actual reason.
 *
 * Precedence:
 *  1. Role-aware 403 copy via {@link resolveForbiddenMessage}.
 *  2. ABP field `validationErrors` (joined) — they say exactly what's wrong.
 *  3. The ABP `error.message` when it is real copy (not an unlocalized
 *     BusinessException code). `error.data.Message` is used when the
 *     top-level message is a localization key.
 *  4. `fallback`, when the only thing available is an opaque/technical/generic placeholder.
 */
import { resolveForbiddenMessage } from './forbiddenErrorMessage';
import { isOpaqueBackendMessage, isRawTechnicalError, isLocalizationKeyMessage } from './bookingErrorMessage';

// Generic labels the generated client injects as ApiError.message (see request.ts
// `catchErrorCodes`). These carry no real information and must never be shown as-is.
const GENERIC_CLIENT_LABELS = new Set([
  'Bad Request',
  'Unauthorized',
  'Forbidden',
  'Not Found',
  'Internal Server Error',
  'Bad Gateway',
  'Service Unavailable',
]);

function isGenericClientMessage(message: string): boolean {
  const trimmed = message.trim();
  return GENERIC_CLIENT_LABELS.has(trimmed) || trimmed.startsWith('Generic Error:');
}

/** The ABP error object (`error.body.error`), as much of it as we read. */
interface AbpError {
  code?: string;
  message?: string;
  data?: { Message?: string; message?: string };
  validationErrors?: Array<{ message?: string }>;
}

/** Shapes the error can arrive in: generated ApiError (`.body`), unwrapped (`.error`), raw axios. */
interface ApiErrorLike {
  body?: { error?: AbpError };
  error?: AbpError;
  response?: { data?: { error?: AbpError } };
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const err = (error ?? {}) as ApiErrorLike;

  // 1. Role-aware 403 handling (returns null for non-403s).
  const forbidden = resolveForbiddenMessage(error);
  if (forbidden) return forbidden;

  // ABP payload across generated-client (`.body`), already-unwrapped (`.error`),
  // and raw axios (`.response.data`) shapes.
  const apiError: AbpError | undefined =
    err.body?.error ?? err.error ?? err.response?.data?.error;

  // 2. Field-level validation errors take precedence.
  const validationErrors = apiError?.validationErrors;
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    const joined = validationErrors
      .map((v) => v?.message)
      .filter((m): m is string => typeof m === 'string' && m.trim().length > 0)
      .join(' ');
    if (joined) return joined;
  }

  // 3. Backend message (UserFriendlyException text). Skip unlocalized
  // BusinessException codes (`Routemize.ExactTime.OutsideWorkHours`) — those
  // are keys, not copy. Prefer `data.Message` when the top-level message is a key.
  const dataMessage =
    typeof apiError?.data?.Message === 'string'
      ? apiError.data.Message
      : typeof apiError?.data?.message === 'string'
        ? apiError.data.message
        : undefined;
  const backendMessage: string | undefined = apiError?.message;
  const usable = (message?: string) =>
    !!message &&
    !isOpaqueBackendMessage(message) &&
    !isRawTechnicalError(message) &&
    !isLocalizationKeyMessage(message);

  if (usable(backendMessage)) {
    return backendMessage as string;
  }
  if (usable(dataMessage)) {
    return dataMessage as string;
  }

  // Last resort before the fallback: a plain Error message, but only if it isn't one
  // of the generated client's generic labels or an opaque/technical placeholder.
  const rawMessage: string | undefined = err instanceof Error ? err.message : undefined;
  if (
    rawMessage &&
    !isGenericClientMessage(rawMessage) &&
    !isOpaqueBackendMessage(rawMessage) &&
    !isRawTechnicalError(rawMessage) &&
    !isLocalizationKeyMessage(rawMessage)
  ) {
    return rawMessage;
  }

  // 4. Nothing meaningful available.
  return fallback;
}
